import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import {
  MatDialogModule,
  MAT_DIALOG_DEFAULT_OPTIONS,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  MatFormFieldModule,
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { NgModule, provideZoneChangeDetection } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { AuthorizeFormComponent } from './pages/terminal/authorize-form/authorize-form.component';
import { CustomerDetailsComponent } from './components/customer/customer-details/customer-details.component';
import { MerchantDetailsComponent } from './components/merchant/merchant-details/merchant-details.component';
import { MerchantsPageComponent } from './pages/merchants/merchants-page.component';
import { PaymentMethodsComponent } from './components/customer/payment-methods/payment-methods.component';
import { ReportingComponent } from './pages/reporting/reporting.component';
import { TerminalComponent } from './pages/terminal/terminal.component';
import { TokenizeFormComponent } from './pages/terminal/tokenize-form/tokenize-form.component';
import { HelpIconComponent } from './components/help-icon/help-icon.component';
import { StreetAddressComponent } from './components/street-address/street-address.component';
import { MerchantContactComponent } from './components/merchant/merchant-contact/merchant-contact.component';
import { EditUserComponent } from './components/user/edit-user/edit-user.component';
import { MerchantAccountInfoComponent } from './components/merchant/merchant-account-info/merchant-account-info.component';
import { BillingInformationComponent } from './components/merchant/merchant-billing-info/merchant-billing-info.component';
import { StatementsComponent } from './pages/merchant-account/merchant-account-statements/statements.component';
import { SupportComponent } from './pages/support/support.component';
import { SignoutComponent } from './pages/signout/signout.component';
import { SigninComponent } from './pages/signin/signin.component';
import { DataPropertyGetterPipe } from './components/pipes/dataPropertyGetterPipe';
import { SafeHtmlPipe } from './components/pipes/safeHtmlPipe';
import { PrimaryNavComponent } from './components/ui/sitenav/primary-nav/primary-nav.component';
import { SecondaryNavComponent } from './components/ui/sitenav/secondary-nav/secondary-nav.component';
import { ChatWindowComponent } from './components/chat-window/chat-window.component';
import { CustomerVaultComponent } from './pages/merchant-account/customer-vault/customer-vault.component';
import { LaunchChatButtonComponent } from './components/chat-window/launch-chat-button/launch-chat-button.component';
import { ChatCenterComponent } from './pages/support/chat-center/chat-center.component';
import { LaunchChatSupportButtonComponent } from './components/chat-window/launch-chat-support-button/launch-chat-support-button.component';
import { HelpMeHostButtonComponent } from './components/help-me/help-me-host-button/help-me-host-button.component';
import { HelpMeClientButtonComponent } from './components/help-me/help-me-client-button/help-me-client-button.component';
import { HelpMePopupComponent } from './components/help-me/help-me-popup/help-me-popup.component';
import { FilterCustomerDialogComponent } from './components/customer/filter-customer-dialog/filter-customer-dialog.component';
import { RecentTransactionsComponent } from './pages/merchant-account/transactions/merchant-transactions-page.component';
import { CustomerTransactionsComponent } from './components/customer/customer-transactions/customer-transactions/customer-transactions.component';
import { MerchantOnboardingComponent } from './components/merchant/merchant-onboarding/merchant-onboarding.component';
import { MerchantApiComponent } from './components/merchant/merchant-api/merchant-api.component';
import { TransactionTableViewComponent } from './components/transaction/transaction-table-view/transaction-table-view.component';
import { OrderDetailsTableViewComponent } from './components/orders/order-details-table-view/order-details-table-view.component';
import { FilterableTableComponent } from './components/ui/filterable-table/filterable-table.component';
import { AdminComponent } from './pages/admin/admin.component';
import { MerchantTableViewComponent } from './components/merchant/merchant-table-view/merchant-table-view.component';
import { UserTableViewComponent } from './components/user/user-table-view/user-table-view.component';
import { CustomerTableViewComponent } from './components/customer/customer-table-view/customer-table-view.component';
import { ErrorDialogComponent } from './core/error-dialog/error-dialog.component';
import { MerchantHeaderComponent } from './components/merchant/merchant-header/merchant-header.component';
import { AddCustomerComponent } from './components/customer/add-customer/add-customer.component';
import { MerchantUsersComponent } from './components/merchant/merchant-users/merchant-users.component';
import { MerchantGroupsComponent } from './components/admin/merchant-groups/merchant-groups.component';
import { MerchantBrandsComponent } from './components/admin/merchant-brands/merchant-brands.component';
import { AccessManagerComponent } from './components/ui/access-manager/access-manager.component';
import { RestrictedComponent } from './pages/error/restricted/restricted.component';
import { BusinessHubPageComponent } from './pages/merchant-account/business-hub-page.component';
import { ImpersonateHeaderComponent } from './components/ui/impersonate-header/impersonate-header/impersonate-header.component';
import { ImpersonateChooserDialogComponent } from './components/user/impersonate-chooser/impersonate-chooser-dialog/impersonate-chooser-dialog.component';
import { AccountOnboardComponent } from './components/account/account-onboard/account-onboard.component';
import { AccountTableViewComponent } from './components/account/account-table-view/account-table-view.component';
import { AccountDetailsFormComponent } from './components/account/account-details-form/account-details-form.component';
import { TransactionDetailsComponent } from './components/transaction/transaction-details/transaction-details.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { GlobalSearchComponent } from './components/global-search/global-search/global-search.component';
import { GlobalSearchTableViewComponent } from './components/global-search/global-search-table-view/global-search-table-view.component';
import { StarComponent } from './components/ui/star/star.component';
import { ProfileButtonComponent } from './components/ui/profile-button/profile-button.component';
import { RoleAdminComponent } from './components/admin/role-admin/role-admin.component';
import { EditRoleComponent } from './components/role/edit-role/edit-role.component';
import { ConfirmDialogComponent } from './core/confirm-dialog/confirm-dialog.component';
import { UsersPageComponent } from './pages/users-page/users-page.component';
import { OmniSearchComponent } from './components/global-search/omni-search/omni-search.component';
import { GlobalSearchFiltersComponent } from './components/global-search/global-search-filters/global-search-filters.component';
import { SiteSearchComponent } from './components/site-search/site-search.component';
import { TextInputComponent } from './components/ui/controls/text-input/text-input.component';
import { DdlInputComponent } from './components/ui/controls/ddl-input/ddl-input.component';
import { CheckInputComponent } from './components/ui/controls/check-input/check-input.component';
import { TerminalCustomerComponent } from './pages/terminal/terminal-customer/terminal-customer.component';
import { TerminalRecurringFormComponent } from './pages/terminal/terminal-recurring-form/terminal-recurring-form.component';
import { TerminalBillingFormComponent } from './pages/terminal/terminal-billing-form/terminal-billing-form.component';
import { TerminalReceiptComponent } from './pages/terminal/terminal-receipt/terminal-receipt.component';
import { MultilineInputComponent } from './components/ui/controls/multiline-input/multiline-input.component';
import { TerminalTransactionsComponent } from './pages/terminal/terminal-transactions/terminal-transactions.component';
import { TerminalOrderComponent } from './pages/terminal/terminal-order/terminal-order.component';
import { TerminalL2L3Component } from './pages/terminal/terminal-L2L3/terminal-L2L3.component';
import { AddCustomerDialogComponent } from './components/customer/add-customer-dialog/add-customer-dialog.component';
import { TerminalShippingFormComponent } from './pages/terminal/terminal-shipping-form/terminal-shipping-form.component';
import { DateInputComponent } from './components/ui/controls/date-input/date-input.component';
import { CustomerAccountComponent } from './pages/merchant-account/customer-account/customer-account.component';
import { TransactionsComponent } from './pages/transactions/transactions.component';
import { TransactionDetailsPageComponent } from './pages/transaction-details-page/transaction-details-page.component';
import { RequiredFieldNoteComponent } from './components/ui/required-field-note/required-field-note.component';
import { TokenizeDialogComponent } from './components/tokenize/tokenize-dialog/tokenize-dialog.component';
import { CustomerInfoComponent } from './components/customer/customer-info/customer-info.component';
import { LineChartComponent } from './components/ui/graphing/line-chart/line-chart.component';
import { TransactionsChartComponent } from './components/charts/transactions-chart/transactions-chart.component';
import { BarChartComponent } from './components/ui/graphing/bar-chart/bar-chart.component';
import { RevenueChartComponent } from './components/charts/revenue-chart/revenue-chart.component';
import { CardtypeChartComponent } from './components/charts/cardtype-chart/cardtype-chart.component';
import { PieChartComponent } from './components/ui/graphing/pie-chart/pie-chart.component';
import { UserFormComponent } from './components/user/user-form/user-form.component';
import { ReroutePageComponent } from './pages/reroute-page/reroute-page.component';
import { FeatureFeedbackComponentComponent } from './components/ui/feedback/feature-feedback-component/feature-feedback-component.component';
import { SuggestionBoxPageComponent } from './pages/suggestion-box-page/suggestion-box-page.component';
import { MerchantProcessorsComponent } from './components/merchant/merchant-processors/merchant-processors.component';
import { MerchantProcessorDialogComponent } from './components/merchant/merchant-processors/editDialog/merchant-processor-dialog/merchant-processor-dialog.component';
import { AccountBillingFormComponent } from './components/account/account-billing-form/account-billing-form.component';
import { AccountInfoFormComponent } from './components/account/account-info-form/account-info-form.component';
import { AccountUsersFormComponent } from './components/account/account-users-form/account-users-form.component';
import { MerchantSupportInfoComponent } from './components/merchant/merchant-support-info/merchant-support-info.component';
import { ChatCenterTableViewComponent } from './components/chat-center/chat-center-table-view/chat-center-table-view.component';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { MasterLayoutComponent } from './layouts/master-layout/master-layout.component';
import { TransactionReportTableViewComponent } from './components/transaction/transaction-report-table-view/transaction-report-table-view.component';
import { MonthlyTransactionSummaryComponent } from './components/transaction/monthly-transaction-summary/monthly-transaction-summary.component';
import { ChargebacksSummaryComponent } from './components/chargebacks/chargebacks-summary/chargebacks-summary.component';
import { ChargebacksTableViewComponent } from './components/chargebacks/chargebacks-table-view/chargebacks-table-view.component';
import { MerchantStatementsListComponent } from './components/merchant/merchant-statements/merchant-statements-list/merchant-statements-list.component';
import { StatementDialogComponent } from './components/merchant/merchant-statements/statement-dialog/statement-dialog.component';
import { UpcomingStatementComponent } from './components/merchant/merchant-statements/upcoming-statement/upcoming-statement.component';
import { WithSpinnerDirective } from './components/ui/loading-spinner/loading-spinner-directive';
import { StyleGuidePageComponent } from './pages/admin/style-guide/style-guide-page/style-guide-page.component';
import { StyleIconographyComponent } from './pages/admin/style-guide/style-guide-page/style-iconography/style-iconography.component';
import { StyleLoadingSpinnersComponent } from './pages/admin/style-guide/style-guide-page/style-loading-spinners/style-loading-spinners.component';
import { CodeSnippetDirective } from './components/ui/code-snippet/code-snippet-directive';
import { StyleFormControlsComponent } from './pages/admin/style-guide/style-guide-page/style-form-controls/style-form-controls.component';
import { StylePageContainersComponent } from './pages/admin/style-guide/style-guide-page/style-page-containers/style-page-containers.component';
import { StyleButtonsComponent } from './pages/admin/style-guide/style-guide-page/style-buttons/style-buttons.component';
import { MerchantChargebacksComponent } from './pages/merchant-account/merchant-chargebacks/merchant-chargebacks.component';
import { SpotlightComponent } from './components/ui/spotlight/spotlight.component';
import { PageHeaderComponent } from './components/ui/page-header/page-header.component';
import { TransactionDialogComponent } from './components/transaction/transaction-dialog/transaction-dialog.component';
import { MerchantTransactionsComponent } from './components/merchant/merchant-transactions/merchant-transactions.component';
import { LeadsPageComponent } from './pages/leads-page/leads-page.component';
import { LeadPageComponent } from './pages/lead-page/lead-page.component';
import { CheckStepsDialogComponent } from './pages/lead-page/check-steps-dialog/check-steps-dialog.component';
import { UploadDialogComponent } from './pages/lead-page/upload-dialog/upload-dialog.component';
import { LogPhoneDialogComponent } from './pages/lead-page/log-phone-dialog/log-phone-dialog.component';
import { LogEmailDialogComponent } from './pages/lead-page/log-email-dialog/log-email-dialog.component';
import { AddNotesDialogComponent } from './pages/lead-page/add-notes-dialog/add-notes-dialog.component';
import { LeadProgressBarComponent } from './pages/lead-page/lead-progress-bar/lead-progress-bar.component';
import { LabelInputComponent } from './components/ui/controls/label-input/label-input.component';
import { OwnerDialogComponent } from './pages/lead-page/owner-dialog/owner-dialog.component';
import { MerchantApplicationComponent } from './components/merchant/merchant-application/merchant-application.component';
import { MerchantSolutionComponent } from './components/merchant/merchant-solution/merchant-solution.component';
import { MerchantAttachmentsComponent } from './components/merchant/merchant-attachments/merchant-attachments.component';
import { ApplicationDialogComponent } from './pages/lead-page/application-dialog/application-dialog.component';
import { ContactFormComponent } from './components/ui/contact-form/contact-form.component';
import { ContactDialogComponent } from './components/ui/dialogs/contact-dialog/contact-dialog.component';
import { AccountNotesComponent } from './components/account-notes/account-notes.component';
import { ButtonInputComponent } from './components/ui/controls/button-input/button-input.component';
import { EditUserGroupComponent } from './components/user/edit-user-group/edit-user-group.component';
import { StylePageComponent } from './pages/style-page/style-page.component';
import { EditUserInfoDialogComponent } from './components/user/edit-user/edit-user-dialog/edit-user-info-dialog.component';
import { AutoFillInputComponent } from './components/ui/controls/auto-fill/auto-fill-input.component';
import { EditMerchantContactDialogComponent } from './components/merchant/dialogs/edit-merchant-contact-dialog.component';
import { UserProfileDialogComponent } from './components/user-profile/user-profile-dialog/user-profile-dialog.component';
import { ResidualsDashboardPageComponent } from './pages/residuals/residuals-dashboard-page/residuals-dashboard-page.component';
import { AddResidualReportDialogComponent } from './components/residuals/add-residual-report-dialog/add-residual-report-dialog.component';
import { MyAccountComponent } from './pages/my-account/my-account.component';
import { BusinessDetailsComponent } from './pages/merchant-account/business-details/business-details.component';
import { MerchantBusinessViewComponent } from './components/merchant/business-view/merchant-business-view/merchant-business-view.component';
import { TerminalLayoutComponent } from './layouts/terminal-layout/terminal-layout.component';
import { MerchantLayoutComponent } from './layouts/merchant-layout/merchant-layout.component';
import { CronPageComponent } from './pages/admin/cron-page/cron-page.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { TestingComponent } from './pages/admin/testing/testing.component';
import { CustomSnackbarComponent } from './components/ui/snackbar/custom-snackbar.component';
import { ManageInvoiceComponent } from './pages/manage-invoice/manage-invoice.component';
import { InvoiceTableViewComponent } from './components/invoices/invoice-table-view/invoice-table-view.component';
import { InvoiceHeaderComponent } from './components/invoices/invoice-header/invoice-header.component';
import { CreateInvoiceComponent } from './pages/create-invoice/create-invoice.component';
import { InvoiceLineItemsComponent } from './components/invoices/invoice-line-items/invoice-line-items.component';
import { InvoicePreviewComponent } from './components/invoices/invoice-preview/invoice-preview.component';
import { CatalogsPageComponent } from './pages/catalogs-page/catalogs-page.component';
import { CatalogItemComponent } from './components/catalog/catalog-item/catalog-item.component';
import { EditCatalogItemComponent } from './components/catalog/edit-catalog-item/edit-catalog-item.component';
import { CatalogComponent } from './components/catalog/catalog/catalog.component';
import { AddCatalogItemDialogComponent } from './components/catalog/dialogs/add-catalog-item-dialog/add-catalog-item-dialog.component';
import { InvoiceSelectItemsComponent } from './components/invoices/invoice-select-items/invoice-select-items.component';
import { EditCatalogComponent } from './components/catalog/edit-catalog/edit-catalog.component';
import { EditCatalogItemDialogComponent } from './components/catalog/dialogs/edit-catalog-item-dialog/edit-catalog-item-dialog.component';
import { PayableInvoiceComponent } from './pages/payable-invoice/payable-invoice.component';
import { InvoiceSettingsComponent } from './pages/invoice-settings/invoice-settings.component';
import { InvoiceNumberingComponent } from './pages/invoice-numbering/invoice-numbering.component';
import { FeatureFlagsComponent } from './components/admin/feature-flags/feature-flags.component';
import { MerchantOrdersPageComponent } from './pages/merchant-account/orders/merchant-orders-page.component';
import { OrdersTableViewComponent } from './components/orders/orders-table-view/orders-table-view.component';
import { OrdersPopupComponent } from './components/orders/orders-popup/orders-popup.component';
import { ApiManagementComponent } from './components/api-management/api-management.component';
import { BootstrapSessionComponent } from './pages/bootstrap-session/bootstrap-session.component';
import { AppFooterModule } from '../../../../libs/Shared/components/swipesum-footer/swipesum-footer.module';
import { AppHeaderModule } from '../../../../libs/Shared/components/swipesum-header/swipesum-header.module';
import { SiteHeader } from './layouts/components/site-header-component/site-header';
import { SidenavLayout } from './layouts/sidenav-layout/sidenav-layout';
import { BusinessHubLayout } from './layouts/business-hub-layout/business-hub-layout';
import { CheckboxModule } from '../../../../libs/Shared/components/inputs/checkbox/checkbox.module';
import { RadioModule } from '../../../../libs/Shared/components/inputs/radio/radio.module';
import { RadioGroupModule } from '../../../../libs/Shared/components/inputs/radiogroup/radio-group.module';
import { TextboxModule } from '../../../../libs/Shared/components/inputs/textbox/textbox.module';
import { MultilineInputComponentModule } from '../../../../libs/Shared/components/inputs/multiline-input/multiline-input.module';
import { DateInputComponentModule } from '../../../../libs/Shared/components/inputs/date-input/date-input.module';
import { SelectboxModule } from '../../../../libs/Shared/components/inputs/selectbox/selectbox.module';
import { TerminalSummaryComponent } from './pages/terminal/terminal-summary/terminal-summary-component';
import { IconModule } from '../../../../libs/Shared/components/icon/icon.module';
import { FindByIdPipe, MapToStringPropPipe, StringInListPipe } from './core/pipes';
import { ProfilePopupComponent } from './layouts/components/profile-popup/profile-popup-component';
import { ProfilePage } from './pages/my-account/profile-page/profile-page';
import { ProfileFormModule } from '../../../../libs/Shared/components/profile-form/profile-form.module';
import { CloseXComponent } from '../../../../libs/Shared/components/close-x/close-x.component';
import { ExternalInvoiceComponent } from './pages/external-invoice/external-invoice.component';
import { SimpleDialogComponent } from '../../../../libs/Shared/components/dialogs/simple-dialog.component';
import { DualAxisChartComponent } from '../../../../libs/Shared/components/graphs/dual-axis-bar-chart.component';
import { DualChartDemoComponent } from '../../../../libs/Shared/components/graphs/dual-chart-demo.component';
import { MerchantTransactionGraphsComponent } from './pages/merchant-graphs/merchant-transaction-graphs.component';
import { AddMerchantContactDialogComponent } from './components/merchant/merchant-contact/add-merchant-contact-dialog.component';

@NgModule({
  declarations: [
    AppComponent,
    AuthorizeFormComponent,
    BillingInformationComponent,
    MerchantAccountInfoComponent,
    CustomerDetailsComponent,
    EditUserComponent,
    HelpIconComponent,
    MerchantsPageComponent,
    MerchantContactComponent,
    MerchantDetailsComponent,
    PaymentMethodsComponent,
    ReportingComponent,
    StreetAddressComponent,
    TerminalComponent,
    TokenizeFormComponent,
    StatementsComponent,
    SupportComponent,
    SignoutComponent,
    SigninComponent,
    DataPropertyGetterPipe,
    SafeHtmlPipe,
    PrimaryNavComponent,
    SecondaryNavComponent,
    ChatWindowComponent,
    CustomerVaultComponent,
    LaunchChatButtonComponent,
    ChatCenterComponent,
    ChatCenterTableViewComponent,
    LaunchChatSupportButtonComponent,
    HelpMeHostButtonComponent,
    HelpMeClientButtonComponent,
    HelpMePopupComponent,
    FilterCustomerDialogComponent,
    RecentTransactionsComponent,
    CustomerTransactionsComponent,
    MerchantOnboardingComponent,
    MerchantApiComponent,
    TransactionTableViewComponent,
    OrderDetailsTableViewComponent,
    FilterableTableComponent,
    AdminComponent,
    MerchantTableViewComponent,
    UserTableViewComponent,
    CustomerTableViewComponent,
    ErrorDialogComponent,
    MerchantHeaderComponent,
    AddCustomerComponent,
    MerchantUsersComponent,
    MerchantGroupsComponent,
    MerchantBrandsComponent,
    AccessManagerComponent,
    RestrictedComponent,
    BusinessHubPageComponent,
    ImpersonateHeaderComponent,
    ImpersonateChooserDialogComponent,
    AccountOnboardComponent,
    AccountTableViewComponent,
    AccountDetailsFormComponent,
    TransactionDetailsComponent,
    DashboardComponent,
    GlobalSearchComponent,
    GlobalSearchTableViewComponent,
    StarComponent,
    CloseXComponent,
    SimpleDialogComponent,
    ProfileButtonComponent,
    RoleAdminComponent,
    EditRoleComponent,
    ConfirmDialogComponent,
    UsersPageComponent,
    OmniSearchComponent,
    GlobalSearchFiltersComponent,
    SiteSearchComponent,
    TextInputComponent,
    DdlInputComponent,
    CheckInputComponent,
    TerminalCustomerComponent,
    TerminalRecurringFormComponent,
    TerminalBillingFormComponent,
    TerminalReceiptComponent,
    MultilineInputComponent,
    TerminalTransactionsComponent,
    TerminalOrderComponent,
    TerminalL2L3Component,
    AddCustomerDialogComponent,
    TerminalShippingFormComponent,
    DateInputComponent,
    CustomerAccountComponent,
    TransactionsComponent,
    TransactionDetailsPageComponent,
    RequiredFieldNoteComponent,
    TokenizeDialogComponent,
    CustomerInfoComponent,
    LineChartComponent,
    TransactionsChartComponent,
    BarChartComponent,
    RevenueChartComponent,
    CardtypeChartComponent,
    PieChartComponent,
    UserFormComponent,
    ReroutePageComponent,
    FeatureFeedbackComponentComponent,
    SuggestionBoxPageComponent,
    MerchantProcessorsComponent,
    MerchantProcessorDialogComponent,
    AccountBillingFormComponent,
    AccountInfoFormComponent,
    AccountUsersFormComponent,
    MerchantSupportInfoComponent,
    MasterLayoutComponent,
    TransactionReportTableViewComponent,
    MonthlyTransactionSummaryComponent,
    ChargebacksSummaryComponent,
    ChargebacksTableViewComponent,
    MerchantStatementsListComponent,
    StatementDialogComponent,
    UpcomingStatementComponent,
    WithSpinnerDirective,
    StyleGuidePageComponent,
    StyleIconographyComponent,
    StyleLoadingSpinnersComponent,
    CodeSnippetDirective,
    StyleFormControlsComponent,
    StylePageContainersComponent,
    StyleButtonsComponent,
    MerchantChargebacksComponent,
    SpotlightComponent,
    PageHeaderComponent,
    TransactionDialogComponent,
    MerchantTransactionsComponent,
    LeadsPageComponent,
    LeadPageComponent,
    CheckStepsDialogComponent,
    UploadDialogComponent,
    LogPhoneDialogComponent,
    LogEmailDialogComponent,
    AddNotesDialogComponent,
    LeadProgressBarComponent,
    LabelInputComponent,
    OwnerDialogComponent,
    MerchantApplicationComponent,
    MerchantSolutionComponent,
    MerchantAttachmentsComponent,
    ApplicationDialogComponent,
    ContactFormComponent,
    ContactDialogComponent,
    AccountNotesComponent,
    ButtonInputComponent,
    EditUserGroupComponent,
    StylePageComponent,
    EditUserInfoDialogComponent,
    AutoFillInputComponent,
    EditMerchantContactDialogComponent,
    UserProfileDialogComponent,
    MerchantDetailsComponent,
    ResidualsDashboardPageComponent,
    AddResidualReportDialogComponent,
    MyAccountComponent,
    BusinessDetailsComponent,
    MerchantBusinessViewComponent,
    TerminalLayoutComponent,
    MerchantLayoutComponent,
    CronPageComponent,
    LoadingSpinnerComponent,
    TestingComponent,
    CustomSnackbarComponent,
    ManageInvoiceComponent,
    InvoiceTableViewComponent,
    InvoiceHeaderComponent,
    TokenizeFormComponent,
    CreateInvoiceComponent,
    InvoiceLineItemsComponent,
    InvoicePreviewComponent,
    CatalogsPageComponent,
    CatalogItemComponent,
    EditCatalogItemComponent,
    CatalogComponent,
    AddCatalogItemDialogComponent,
    InvoiceSelectItemsComponent,
    EditCatalogComponent,
    EditCatalogItemDialogComponent,
    PayableInvoiceComponent,
    InvoiceSettingsComponent,
    InvoiceNumberingComponent,
    FeatureFlagsComponent,
    MerchantOrdersPageComponent,
    OrdersTableViewComponent,
    OrdersPopupComponent,
    ApiManagementComponent,
    BootstrapSessionComponent,
    SiteHeader,
    SidenavLayout,
    BusinessHubLayout,
    TerminalSummaryComponent,
    ProfilePopupComponent,
    ProfilePage,
    ExternalInvoiceComponent,
    DualAxisChartComponent,
    DualChartDemoComponent,
    MerchantTransactionGraphsComponent,
    AddMerchantContactDialogComponent
  ],
  bootstrap: [AppComponent],
  // entryComponents: [TokenizeFormComponent], //TableViewComponent
  imports: [
    AppRoutingModule,
    IconModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    DragDropModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    MatTableModule,
    MatTabsModule,
    MatIconModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatBadgeModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    AppHeaderModule,
    AppFooterModule,
    CheckboxModule,
    RadioModule,
    RadioGroupModule,
    TextboxModule,
    MultilineInputComponentModule,
    DateInputComponentModule,
    SelectboxModule,
    IconModule,
    MapToStringPropPipe,
    StringInListPipe,
    FindByIdPipe,
    ProfileFormModule,

  ],
  providers: [
    provideAnimations(),
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: MatDialogRef, useValue: {} },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'always' },
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection(),
  ],
})
export class AppModule {}
